import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import hotkeys from 'hotkeys-js';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as editCustomerSelectors from '@makemydeal/dr-dash-store/selectors/editCustomerSelectors';
import * as customerUIActionCreators from '@makemydeal/dr-dash-store/actions/customerUIActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { BUYER } from '@makemydeal/dr-shared-types';
import {
    initializeHotkeys,
    isBlockingModalOpen,
    closeOpenSnackbars,
    createHotkeyWithCapsLock
} from '@makemydeal/dr-dash-components';

/**
 * Hook to register Buyer slideout hotkey (Alt+B+I) for compact UI mode.
 * Opens the Buyer edit slideout when a customer exists.
 * Supports toggle behavior - pressing again closes the slideout.
 */
export const useBuyerHotkey = (enabled = true): void => {
    const dispatch = useDispatch();
    const currentCustomerSlideout = useSelector(editCustomerSelectors.showEditCustomerSlideout);
    const commonConsumerId = useSelector(offerReduxSelectors.getShopperCommonConsumerId);

    // Use ref to always have the latest value in the hotkey handler
    const currentCustomerSlideoutRef = useRef(currentCustomerSlideout);

    useEffect(() => {
        currentCustomerSlideoutRef.current = currentCustomerSlideout;
    }, [currentCustomerSlideout]);

    // Lazily initialize hotkey string
    const BUYER_HOTKEY = useMemo(() => createHotkeyWithCapsLock('b+i'), []);

    useEffect(() => {
        // Don't register hotkey if disabled or no customer exists
        if (!enabled || !commonConsumerId) {
            return undefined;
        }

        initializeHotkeys();

        hotkeys(BUYER_HOTKEY, function (event): void {
            event.preventDefault();

            if (isBlockingModalOpen()) {
                return;
            }

            closeOpenSnackbars();

            // Toggle: if Buyer slideout is already open, close it; otherwise open it
            if (currentCustomerSlideoutRef.current === BUYER) {
                dispatch(editCustomerActionCreators.hideEditCustomerSlideout());
            } else {
                // Close all other panels first before opening Buyer slideout
                dispatch(baseDealActionCreators.closeSlideOut());
                dispatch(customerUIActionCreators.showCustomerSearch(false));
                dispatch(editCustomerActionCreators.hideEditCustomerSlideout()); // Close CoBuyer if open
                dispatch(editCustomerActionCreators.showEditBuyerSlideout());
            }
        });

        return () => {
            hotkeys.unbind(BUYER_HOTKEY);
        };
    }, [dispatch, enabled, commonConsumerId, BUYER_HOTKEY]);
};
